import {
  ExposureSchema,
  type Exposure,
  ApplicationProofPathsSchema,
  type ApplicationProofPaths,
  AcceptProposalsRequestSchema,
  DismissProposalRequestSchema,
  ListApplicationProposalsResponseSchema,
  type AcceptProposalsRequest,
  type ListApplicationProposalsResponse,
  ListRemediationsResponseSchema,
  type Remediation,
  AgentRunSchema,
  ApplicationSchema,
  ApprovalSchema,
  CreateAgentRunRequestSchema,
  CreateApprovalRequestSchema,
  ListAgentRunsResponseSchema,
  CreateScanRequestSchema,
  ListApprovalsResponseSchema,
  ListAuditResponseSchema,
  ListFindingsResponseSchema,
  ListScansResponseSchema,
  PendingApprovalsResponseSchema,
  ScanSchema,
  ListScopeVersionsResponseSchema,
  PutScopeRequestSchema,
  ScopeVersionSchema,
  CreateApplicationRequestSchema,
  CreateEngagementRequestSchema,
  EngagementSchema,
  ListApplicationsResponseSchema,
  ListEngagementsResponseSchema,
  ProblemSchema,
  FindingSchema,
  GetReportResponseSchema,
  ListReportsResponseSchema,
  TriageFindingRequestSchema,
  TransitionEngagementRequestSchema,
  PatchApplicationRequestSchema,
  CloudConnectionSchema,
  VulnerabilityIntelSchema,
  KnowledgeStatusSchema,
  type VulnerabilityIntel,
  type KnowledgeStatus,
  ApplicationCloudGraphSchema,
  DiscoverySchema,
  ListDiscoveriesResponseSchema,
  type ApplicationCloudGraph,
  type Discovery,
  CreateCloudConnectionRequestSchema,
  ListCloudConnectionsResponseSchema,
  RotateCloudConnectionRequestSchema,
  type CloudConnection,
  type CreateCloudConnectionRequest,
  type RotateCloudConnectionRequest,
  SurfaceMapSchema,
  type Application,
  type SurfaceMap,
  type CreateEngagementRequest,
  type Engagement,
  type AgentRun,
  type Approval,
  type CreateAgentRunRequest,
  type CreateApprovalRequest,
  type CreateScanRequest,
  type AuditEvent,
  type Finding,
  type PendingApproval,
  type Scan,
  type PutScopeRequest,
  type ScopeVersion,
  type GetReportResponse,
  type ReportMeta,
  type TriageFindingRequest,
  type TransitionEngagementRequest,
} from '@ai-secure-twin/shared';
import { fetchAuthSession } from 'aws-amplify/auth';
import { z } from 'zod';
import { getRuntimeConfig } from './config';

/**
 * Typed client for the v1 API (§6.3).
 *
 * Every response is parsed with the shared schema rather than cast, so a server that drifts
 * from the contract fails here with a clear message instead of rendering as `undefined`
 * somewhere deep in a component.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    /** Field-level detail from a 400, when the server sent any. */
    public readonly fieldErrors?: { path: string; message: string }[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The ID token, not the access token: the API Gateway authorizer is configured against the
 * user pool and the handlers read `custom:orgId` and `cognito:groups`, which only the ID
 * token carries.
 */
async function authorization(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new ApiError('Your session has expired. Please sign in again.', 401);
  return `Bearer ${token}`;
}

async function request<T extends z.ZodTypeAny>(
  path: string,
  schema: T,
  init: RequestInit = {},
): Promise<z.infer<T>> {
  const response = await fetch(`${getRuntimeConfig().apiUrl}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      authorization: await authorization(),
      ...(init.body ? { 'content-type': 'application/json' } : {}),
    },
  });

  if (!response.ok) throw await toApiError(response);

  const parsed = schema.safeParse(await response.json());
  if (!parsed.success) {
    throw new ApiError('The server returned a response this app does not understand.', 500);
  }
  return parsed.data;
}

/**
 * Turns a failed response into an `ApiError`.
 *
 * The API answers with RFC 9457 problem+json, but a WAF block or a gateway error does not —
 * the edge returns its own JSON, so the body is parsed defensively and falls back to a
 * message written for the person reading it rather than the raw payload.
 */
async function toApiError(response: Response): Promise<ApiError> {
  const body: unknown = await response.json().catch(() => null);
  const problem = ProblemSchema.safeParse(body);

  if (problem.success) {
    return new ApiError(problem.data.title, response.status, problem.data.errors);
  }

  if (response.status === 403) {
    // A WAF block looks like this: {"message":"Forbidden"} with no problem+json.
    return new ApiError(
      'That request was blocked. If it contained a URL or an IP address, the edge firewall may have rejected it.',
      403,
    );
  }
  return new ApiError(`The request failed (HTTP ${response.status}).`, response.status);
}

export const api = {
  listApplications: (): Promise<{ items: Application[]; nextCursor?: string }> =>
    request('/v1/applications', ListApplicationsResponseSchema),

  /**
   * `async` so a client-side validation failure *rejects* rather than throwing before the
   * promise exists. A caller should have one failure path, not two.
   */
  createApplication: async (
    body: z.input<typeof CreateApplicationRequestSchema>,
  ): Promise<Application> =>
    request('/v1/applications', ApplicationSchema, {
      method: 'POST',
      body: JSON.stringify(CreateApplicationRequestSchema.parse(body)),
    }),

  getApplication: (id: string): Promise<Application> =>
    request(`/v1/applications/${id}`, ApplicationSchema),

  /** Carries `expectedVersion`, so an edit made against a stale read is a 409, not a lost update. */
  patchApplication: async (
    id: string,
    body: z.input<typeof PatchApplicationRequestSchema>,
  ): Promise<Application> =>
    request(`/v1/applications/${id}`, ApplicationSchema, {
      method: 'PATCH',
      body: JSON.stringify(PatchApplicationRequestSchema.parse(body)),
    }),

  listCloudConnections: (): Promise<{ items: CloudConnection[] }> =>
    request('/v1/cloud-connections', ListCloudConnectionsResponseSchema),

  /** The key goes to the API once, over TLS, and is never returned by any call. */
  createCloudConnection: async (body: CreateCloudConnectionRequest): Promise<CloudConnection> =>
    request('/v1/cloud-connections', CloudConnectionSchema, {
      method: 'POST',
      body: JSON.stringify(CreateCloudConnectionRequestSchema.parse(body)),
    }),

  verifyCloudConnection: (id: string, expectedVersion: number): Promise<CloudConnection> =>
    request(`/v1/cloud-connections/${id}/verify`, CloudConnectionSchema, {
      method: 'POST',
      body: JSON.stringify({ expectedVersion }),
    }),

  rotateCloudConnection: async (
    id: string,
    body: RotateCloudConnectionRequest,
  ): Promise<CloudConnection> =>
    request(`/v1/cloud-connections/${id}/rotate`, CloudConnectionSchema, {
      method: 'POST',
      body: JSON.stringify(RotateCloudConnectionRequestSchema.parse(body)),
    }),

  revokeCloudConnection: (id: string, expectedVersion: number): Promise<CloudConnection> =>
    request(`/v1/cloud-connections/${id}/revoke`, CloudConnectionSchema, {
      method: 'POST',
      body: JSON.stringify({ expectedVersion }),
    }),

  /** Source facts about a finding: CWE, CVEs with CVSS / EPSS / KEV, fixed versions, priority. */
  getFindingIntel: (engagementId: string, findingId: string): Promise<VulnerabilityIntel> =>
    request(
      `/v1/engagements/${engagementId}/findings/${findingId}/intel`,
      VulnerabilityIntelSchema,
    ),

  refreshFindingIntel: (engagementId: string, findingId: string): Promise<unknown> =>
    request(`/v1/engagements/${engagementId}/findings/${findingId}/intel/refresh`, z.unknown(), {
      method: 'POST',
    }),

  getKnowledgeStatus: (): Promise<KnowledgeStatus> =>
    request('/v1/knowledge/status', KnowledgeStatusSchema),

  listDiscoveries: (connectionId: string): Promise<{ items: Discovery[] }> =>
    request(`/v1/cloud-connections/${connectionId}/discoveries`, ListDiscoveriesResponseSchema),

  startDiscovery: (connectionId: string): Promise<Discovery> =>
    request(`/v1/cloud-connections/${connectionId}/discoveries`, DiscoverySchema, {
      method: 'POST',
    }),

  /** Applications proposed from the connection's latest discovery, and the shadow surface. */
  listProposals: (connectionId: string): Promise<ListApplicationProposalsResponse> =>
    request(
      `/v1/cloud-connections/${connectionId}/proposals`,
      ListApplicationProposalsResponseSchema,
    ),

  /** The server rebuilds the application from the discovery; only keys, name and criticality go. */
  acceptProposals: async (
    connectionId: string,
    body: z.input<typeof AcceptProposalsRequestSchema>,
  ): Promise<Application> =>
    request(`/v1/cloud-connections/${connectionId}/proposals/accept`, ApplicationSchema, {
      method: 'POST',
      body: JSON.stringify(
        AcceptProposalsRequestSchema.parse(body) satisfies AcceptProposalsRequest,
      ),
    }),

  dismissProposal: (
    connectionId: string,
    key: string,
    expectedVersion: number,
  ): Promise<CloudConnection> =>
    request(`/v1/cloud-connections/${connectionId}/proposals/dismiss`, CloudConnectionSchema, {
      method: 'POST',
      body: JSON.stringify(DismissProposalRequestSchema.parse({ key, expectedVersion })),
    }),

  /** The organisation's Exposure Debt and the CTEM loop's figures (ADR 0010). */
  getExposure: (): Promise<Exposure> => request('/v1/exposure', ExposureSchema),

  /** The application's findings traced through its cloud account, ranked by reach (ADR 0009). */
  getProofPaths: (applicationId: string): Promise<ApplicationProofPaths> =>
    request(`/v1/applications/${applicationId}/proof-paths`, ApplicationProofPathsSchema),

  /** The latest discovered graph of the application's cloud account, with its seed. */
  getCloudGraph: (applicationId: string): Promise<ApplicationCloudGraph> =>
    request(`/v1/applications/${applicationId}/cloud-graph`, ApplicationCloudGraphSchema),

  /** What scans have observed of the application, placed on its declared components. */
  getSurfaceMap: (id: string): Promise<SurfaceMap> =>
    request(`/v1/applications/${id}/surface`, SurfaceMapSchema),

  listEngagements: (): Promise<{ items: Engagement[]; nextCursor?: string }> =>
    request('/v1/engagements', ListEngagementsResponseSchema),

  getEngagement: (id: string): Promise<Engagement> =>
    request(`/v1/engagements/${id}`, EngagementSchema),

  /** Validated client-side first, so an obvious mistake never becomes a round trip. */
  createEngagement: async (body: CreateEngagementRequest): Promise<Engagement> =>
    request('/v1/engagements', EngagementSchema, {
      method: 'POST',
      body: JSON.stringify(CreateEngagementRequestSchema.parse(body)),
    }),

  /** Every version ever written, newest last. Scope history is append-only (§5). */
  listScopeVersions: (engagementId: string): Promise<{ items: ScopeVersion[] }> =>
    request(`/v1/engagements/${engagementId}/scope`, ListScopeVersionsResponseSchema),

  /**
   * Writes the next scope version.
   *
   * Parsed with the same schema the server uses, so a mistake is reported in the form rather
   * than surviving a round trip to come back as a 400.
   */
  putScope: async (engagementId: string, body: PutScopeRequest): Promise<ScopeVersion> =>
    request(`/v1/engagements/${engagementId}/scope`, ScopeVersionSchema, {
      method: 'PUT',
      body: JSON.stringify(PutScopeRequestSchema.parse(body)),
    }),

  /** Everything in the tenant currently waiting at a gate. */
  listPendingApprovals: (): Promise<{ items: PendingApproval[] }> =>
    request('/v1/approvals', PendingApprovalsResponseSchema),

  listApprovals: (engagementId: string): Promise<{ items: Approval[] }> =>
    request(`/v1/engagements/${engagementId}/approvals`, ListApprovalsResponseSchema),

  /**
   * Records a decision.
   *
   * `gate` and `scopeVersion` say what the approver was looking at; the server refuses the
   * decision if either has moved on, rather than applying it to something they never saw.
   */
  decideApproval: async (engagementId: string, body: CreateApprovalRequest): Promise<Approval> =>
    request(`/v1/engagements/${engagementId}/approvals`, ApprovalSchema, {
      method: 'POST',
      body: JSON.stringify(CreateApprovalRequestSchema.parse(body)),
    }),

  listAgentRuns: (engagementId: string): Promise<{ items: AgentRun[] }> =>
    request(`/v1/engagements/${engagementId}/agent-runs`, ListAgentRunsResponseSchema),

  getAgentRun: (engagementId: string, runId: string): Promise<AgentRun> =>
    request(`/v1/engagements/${engagementId}/agent-runs/${runId}`, AgentRunSchema),

  /** Answers 202: the run is queued, and the caller polls `getAgentRun` for the result. */
  requestAgentRun: async (engagementId: string, body: CreateAgentRunRequest): Promise<AgentRun> =>
    request(`/v1/engagements/${engagementId}/agent-runs`, AgentRunSchema, {
      method: 'POST',
      body: JSON.stringify(CreateAgentRunRequestSchema.parse(body)),
    }),

  listScans: (engagementId: string): Promise<{ items: Scan[] }> =>
    request(`/v1/engagements/${engagementId}/scans`, ListScansResponseSchema),

  /**
   * Starts a scan against a named scope version.
   *
   * The server refuses unless that version is approved, the engagement is past its scope
   * gate, and the moment is inside the approved window — none of which this client checks,
   * because none of them are the client's to decide.
   */
  startScan: async (engagementId: string, body: CreateScanRequest): Promise<Scan> =>
    request(`/v1/engagements/${engagementId}/scans`, ScanSchema, {
      method: 'POST',
      body: JSON.stringify(CreateScanRequestSchema.parse(body)),
    }),

  /**
   * Asks to move an engagement on. `from` is the stage this screen was showing; the server
   * refuses if the engagement has moved since, and refuses with the real reason if the move is
   * not allowed yet — the button's disabled state is a courtesy, not the rule.
   */
  transitionEngagement: async (
    engagementId: string,
    body: TransitionEngagementRequest,
  ): Promise<Engagement> =>
    request(`/v1/engagements/${engagementId}/transitions`, EngagementSchema, {
      method: 'POST',
      body: JSON.stringify(TransitionEngagementRequestSchema.parse(body)),
    }),

  listFindings: (engagementId: string): Promise<{ items: Finding[]; nextCursor?: string }> =>
    request(`/v1/engagements/${engagementId}/findings`, ListFindingsResponseSchema),

  /**
   * Records a triage decision. `from` is the status this screen showed; the server refuses if
   * someone has decided since, rather than letting the second decision silently win.
   */
  triageFinding: async (
    engagementId: string,
    findingId: string,
    body: TriageFindingRequest,
  ): Promise<Finding> =>
    request(`/v1/engagements/${engagementId}/findings/${findingId}`, FindingSchema, {
      method: 'PATCH',
      body: JSON.stringify(TriageFindingRequestSchema.parse(body)),
    }),

  /** Fixes Guardian proposed for this engagement's findings, with their pull requests and proof. */
  listRemediations: (engagementId: string): Promise<{ items: Remediation[] }> =>
    request(`/v1/engagements/${engagementId}/remediations`, ListRemediationsResponseSchema),

  listReports: (engagementId: string): Promise<{ items: ReportMeta[] }> =>
    request(`/v1/engagements/${engagementId}/reports`, ListReportsResponseSchema),

  /** The report and the server's verdict on whether its stored bytes still match their hash. */
  getReport: (engagementId: string, version: number): Promise<GetReportResponse> =>
    request(`/v1/engagements/${engagementId}/reports/${version}`, GetReportResponseSchema),

  /** Security-admin only; anything else is refused by the server with a 403. */
  listAudit: (engagementId: string): Promise<{ items: AuditEvent[] }> =>
    request(`/v1/engagements/${engagementId}/audit`, ListAuditResponseSchema),
};
